import { InputAdornment, styled, TextField, type TextFieldProps } from '@mui/material';
import { type FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { type UseCachedValidationProps } from '@@form/hooks/useCachedValidation';
import { type FormFieldError } from '@@form/hooks/useReactHookFormFieldError';
import { getErrorMessage } from '@@form/utils/getErrorMessage';
import { getCharacterCount } from '@@plate/utils/characterCount';

const StyledInputAdornment = styled(InputAdornment)(({ theme }) => ({
    minWidth: theme.spacing(3),
}));

type CountProps = {
    totalCharacterCount: number;
    displayUsedCharacters?: boolean;
    displayRemainingCharacters?: number;
};

const getCount = ({
    totalCharacterCount,
    displayRemainingCharacters,
    displayUsedCharacters,
}: CountProps) => {
    if (!displayUsedCharacters && !displayRemainingCharacters) {
        return undefined;
    }

    if (displayRemainingCharacters) {
        return `${totalCharacterCount}/${displayRemainingCharacters}`;
    }

    return totalCharacterCount > 0 ? totalCharacterCount.toString() : undefined;
};

export type TextInputProps = Omit<TextFieldProps, 'error'> & {
    error?: FormFieldError | FieldError;
    validate?: UseCachedValidationProps['validate'];
    displayUsedCharacters?: boolean;
    preserveErrorSpace?: boolean;
} & Omit<CountProps, 'totalCharacterCount'>;

const TextInput = (props: TextInputProps) => {
    const {
        error,
        preserveErrorSpace,
        displayUsedCharacters,
        displayRemainingCharacters,
        slotProps,
        ...rest
    } = props;

    const { t } = useTranslation();
    const totalCharacterCount = getCharacterCount(rest.value);

    const errorMessage = getErrorMessage(error, t);
    const helperText = errorMessage || props.helperText || (preserveErrorSpace && ' ');

    const count = getCount({
        totalCharacterCount,
        displayRemainingCharacters,
        displayUsedCharacters,
    });

    return (
        <TextField
            {...rest}
            error={Boolean(error)}
            helperText={helperText}
            slotProps={{
                ...slotProps,
                input: {
                    ...slotProps?.input,
                    ...(count && {
                        endAdornment: (
                            <StyledInputAdornment position="end">{count}</StyledInputAdornment>
                        ),
                    }),
                },
            }}
        />
    );
};

export default TextInput;
